import React, { ReactElement, ReactNode } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { toFixedHours } from '@app/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/ui/tooltip';
import {
  DueTone,
  IncidentView,
  OpenIncidentView,
  Priority,
  SlaOutcome,
  dueTone,
  formatElapsed,
  formatTarget,
} from './sources/incidents';

/** The two incident tables, shared by the organisation-wide page and the per-product tab. */

// Red is a missed promise at any priority here, unlike the priority pill where only P1 earns it.
const OUTCOME_STYLE: Record<SlaOutcome, string> = {
  met: 'text-emerald-600 dark:text-emerald-400',
  missed: 'text-red-600 dark:text-red-400',
  pending: 'text-muted-foreground',
};

// Inset off the row's full-width wash: a pill flush with the tint's edge reads as sitting on the join.
const FIRST_CELL = 'pl-3';
const LAST_CELL = 'pr-3';

const DUE_STYLE: Record<DueTone, string> = {
  overdue: 'font-semibold text-red-600 dark:text-red-400',
  soon: 'font-medium text-amber-600 dark:text-amber-400',
  ok: 'text-muted-foreground',
};

const dateTime = (isoInstant: string, language: string): string =>
  new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(
    new Date(isoInstant),
  );

// The year only once it is not this one, so a long list of recent incidents stays short.
const reportedDate = (isoInstant: string, language: string): string => {
  const at = new Date(isoInstant);
  const year = at.getFullYear() === new Date().getFullYear() ? undefined : 'numeric';
  return new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'short',
    year,
    hour: '2-digit',
    minute: '2-digit',
  }).format(at);
};

const two = (value: number): string => String(value).padStart(2, '0');

/** The moment something happened, split into the hour and the day it belongs to. */
const clockOf = (isoInstant: string): string => {
  const at = new Date(isoInstant);
  return `${two(at.getHours())}:${two(at.getMinutes())}`;
};

const dayOf = (isoInstant: string): string => {
  const at = new Date(isoInstant);
  return `${two(at.getDate())}/${two(at.getMonth() + 1)}/${at.getFullYear()}`;
};

/** A line in either table, carrying the product it is on where the table spans more than one. */
export interface IncidentRow<T extends IncidentView = IncidentView> {
  incident: T;
  productId?: string;
  productName?: string;
}

interface TableProps<T extends IncidentView> {
  rows: IncidentRow<T>[];
  /** Passed rather than inferred, so an empty table lines its headers up with a full one. */
  showProduct: boolean;
  /** Where the row goes when clicked. Rows are inert without it. */
  onOpen?: (row: IncidentRow<T>) => void;
  /** Buttons for moving the record along from the row. Nothing passes it in this slice. */
  actions?: (row: IncidentRow<T>) => ReactNode;
}

// `IncidentView`, not `OpenIncidentView`: `buildOpenIncidents` filters on `resolvedAt` without narrowing the type.
type OpenTableProps = TableProps<IncidentView>;

/** What a figure that does not exist yet reads as, in every column that can carry one. */
const DASH = '—';

const headClass = 'whitespace-nowrap border-b border-border text-xs uppercase tracking-wider text-muted-foreground';

// Fixed layout: left to itself the table fits the longest subject and pushes the figures off the card.
const tableClass = 'w-full table-fixed text-sm';

/** How much of the table each column gets, as a share of the whole. */
const reportShares = (showProduct: boolean, hasActions = false, hasGo = true): [string, number][] => [
  ['priority', 1],
  ...(showProduct ? ([['product', 1.5]] as [string, number][]) : []),
  ['what', 2.6],
  ['reported', 1.7],
  ['answeredIn', 1.3],
  ['resolvedIn', 1.3],
  ['hours', 1.3],
  // What it holds is three words on the resolved half: `Resolved 18:06`, and the date small behind it.
  ['state', 1.8],
  ...(hasActions ? ([['stateActions', 1.7]] as [string, number][]) : []),
  ...(hasGo ? ([['go', 0.4]] as [string, number][]) : []),
];

const Columns = ({ shares }: { shares: [string, number][] }): ReactElement => {
  const total = shares.reduce((sum, [, share]) => sum + share, 0);
  return (
    <colgroup>
      {shares.map(([name, share]) => (
        <col key={name} style={{ width: `${((share / total) * 100).toFixed(3)}%` }} />
      ))}
    </colgroup>
  );
};

/** What the incident cost us, carrying its unit. */
const HoursCell = ({ hours, className }: { hours: number; className?: string }): ReactElement => (
  <td className={cx('py-2.5 text-right tabular-nums text-foreground', className)}>{`${toFixedHours(hours)}h`}</td>
);

const rowKey = (row: IncidentRow<IncidentView>): string => `${row.productId ?? ''}:${row.incident.id}`;

/** The priority, with a dot when nobody has checked what it commits us to. */
const PriorityBadge = ({ priority }: { priority: Priority }): ReactElement => (
  <span className="text-xs font-semibold text-muted-foreground">{priority}</span>
);

/** What happened, cut off at the column rather than wrapping. */
const Title = ({ incident }: { incident: IncidentView }): ReactElement => (
  // The flex box stays: it is what gives the truncating span a width to be cut off at.
  <div className="flex min-w-0 items-center">
    <span className="truncate text-foreground" title={incident.title}>
      {incident.title}
    </span>
  </div>
);

/** When the client raised it, which is what a reader scanning the list dates it by. */
const ReportedCell = ({ incident }: { incident: IncidentView }): ReactElement => {
  const {
    i18n: { language },
  } = useTranslation();
  return (
    <td className="whitespace-nowrap py-2.5 pr-6 text-right tabular-nums text-muted-foreground">
      {reportedDate(incident.reportedAt, language)}
    </td>
  );
};

/** Elapsed time against its target, coloured by whether it kept the promise. */
const ElapsedCell = ({
  minutes,
  outcome,
  targetHours,
  pending,
}: {
  minutes: number | null;
  outcome: SlaOutcome;
  /** Null once the product is off support: the figure is real, the promise it was measured against is gone. */
  targetHours: number | null;
  /** What a duration that has not happened yet reads as: a dash, there being nothing to measure. */
  pending?: string;
}): ReactElement => {
  const { t } = useTranslation();
  if (minutes === null) return <span className="text-muted-foreground">{pending ?? t('incidents.pending')}</span>;
  // How long it took, with no verdict on it: a tooltip naming a target would be inventing one.
  if (targetHours === null) return <span className="tabular-nums text-muted-foreground">{formatElapsed(minutes)}</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cx('cursor-default tabular-nums', OUTCOME_STYLE[outcome])}>{formatElapsed(minutes)}</span>
      </TooltipTrigger>
      <TooltipContent>
        {t(outcome === 'met' ? 'incidents.withinTarget' : 'incidents.pastTarget', {
          target: formatTarget(targetHours),
        })}
      </TooltipContent>
    </Tooltip>
  );
};

/** What is left of the resolution target, or how far past it this is. */
const TimeElapsedCell = ({ incident }: { incident: OpenIncidentView }): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Off support: the incident is still ours to fix, but no promise says by when.
  if (!incident.target || incident.open.resolutionDueInMinutes === null) {
    return <span className="text-muted-foreground">{t('incidents.noAgreement')}</span>;
  }

  const tone = dueTone(incident.open.resolutionDueInMinutes, incident.target.resolutionHours);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Past the promise, "overdue" replaces the figure rather than joining it; the amount stays a hover away. */}
        <span className={cx('cursor-default tabular-nums', DUE_STYLE[tone])}>
          {tone === 'overdue' ? t('incidents.statusOverdue') : formatElapsed(incident.open.resolutionDueInMinutes)}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {t('incidents.cameInAt', { at: dateTime(incident.reportedAt, language) })}
        {tone !== 'ok' &&
          ` · ${t(tone === 'overdue' ? 'incidents.overdueByAgainst' : 'incidents.dueInAgainst', {
            time: formatElapsed(Math.abs(incident.open.resolutionDueInMinutes)),
            target: formatTarget(incident.target.resolutionHours),
          })}`}
      </TooltipContent>
    </Tooltip>
  );
};

/** That the thing is fixed, on the rows where it is — and nothing at all on the rows where it is not. */
const StatusCell = ({ incident, at }: { incident: IncidentView; at?: boolean }): ReactElement | null => {
  const { t } = useTranslation();

  if (!incident.resolvedAt) return null;

  // On a report the pill carries the moment, at the row's own size, with the date a step down behind it.
  if (at) {
    return (
      // Same shape as the open side: the phrase at the row's size, the date a step down behind it.
      <span className="whitespace-nowrap text-foreground">
        {t('incidents.resolvedStamp', { at: clockOf(incident.resolvedAt) })}{' '}
        <span className="text-[10px] font-normal text-muted-foreground">{dayOf(incident.resolvedAt)}</span>
      </span>
    );
  }

  return <span className="whitespace-nowrap text-muted-foreground">{t('incidents.statusResolved')}</span>;
};

/** The state and the clock as one answer: neither half means much without the other. */
const StateCell = ({ incident, at }: { incident: IncidentView; at?: boolean }): ReactElement => (
  <td className="py-2.5">
    <div className="flex items-center justify-end gap-3">
      <StatusCell incident={incident} at={at} />
      {incident.open && <TimeElapsedCell incident={incident as OpenIncidentView} />}
    </div>
  </td>
);

/** Says the row leads somewhere. Trailing, where every other table in the app puts its chevron. */
const GoCell = (): ReactElement => (
  <td className={cx('py-2.5 text-right print:hidden', LAST_CELL)}>
    <ChevronRight className="ml-auto size-4 shrink-0 text-muted-foreground" />
  </td>
);

/** The cell a row's state buttons sit in, kept apart from the pencil and the bin: state is not an edit. */
const StateActionsCell = ({ children }: { children: ReactNode }): ReactElement => (
  <td className="py-1.5 pl-2 text-right print:hidden" onClick={event => event.stopPropagation()}>
    <div className="flex justify-end">{children}</div>
  </td>
);

/** What is still running: how long the client has been waiting, and whether anybody has answered. */
export const OpenIncidentTable = ({
  rows,
  showProduct,
  outcomes = false,
  onOpen,
  actions,
}: OpenTableProps & {
  /** Whether to carry how long the answer and the fix took, the two columns the resolved table has. */
  outcomes?: boolean;
}): ReactElement => {
  const { t } = useTranslation();

  return (
    <table className={tableClass}>
      <Columns
        shares={
          outcomes
            ? reportShares(showProduct, !!actions, !!onOpen)
            : [
                ['priority', 1],
                ...(showProduct ? ([['product', 1.5]] as [string, number][]) : []),
                ['what', 3],
                ['reported', 1.9],
                ['hours', 1.5],
                ['state', 1.3],
                ...(actions ? ([['stateActions', 1.7]] as [string, number][]) : []),
                ...(onOpen ? ([['go', 0.4]] as [string, number][]) : []),
              ]
        }
      />
      <thead>
        <tr className={headClass}>
          <th className={cx('pb-2 pr-4 text-left font-semibold', FIRST_CELL)}>{t('incidents.priority')}</th>
          {showProduct && <th className="pb-2 pr-4 text-left font-semibold">{t('incidents.product')}</th>}
          <th className="pb-2 pr-4 text-left font-semibold">{t('incidents.issueColumn')}</th>
          <th className="pb-2 pr-6 text-right font-semibold">{t('incidents.reportedAt')}</th>
          {outcomes && (
            <>
              <th className="pb-2 text-right font-semibold">{t('incidents.answeredIn')}</th>
              <th className="pb-2 text-right font-semibold">{t('incidents.resolvedIn')}</th>
            </>
          )}
          {/* The headline reads best last, next to the way out. */}
          <th className="pb-2 pr-6 text-right font-semibold">{t('incidents.hoursReported')}</th>
          {/* Right-aligned: these are read by comparing them, which a ragged left edge defeats. */}
          <th className="pb-2 text-right font-semibold">{t('incidents.timeLeft')}</th>
          {actions && (
            <th className="pb-2 print:hidden">
              <span className="sr-only">{t('incidents.rowActions')}</span>
            </th>
          )}
          {onOpen && (
            <th className={cx('pb-2 print:hidden', LAST_CELL)}>
              <span className="sr-only">{t('incidents.showWork')}</span>
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr
            key={rowKey(row)}
            className={cx('incident-row', onOpen && 'cursor-pointer', onOpen && 'hover:bg-muted/40')}
            onClick={() => onOpen?.(row)}
          >
            <td className={cx('py-2.5 pr-4', FIRST_CELL)}>
              <PriorityBadge priority={row.incident.priority} />
            </td>
            {showProduct && <td className="truncate py-2.5 pr-4 text-muted-foreground">{row.productName}</td>}
            <td className="py-2.5 pr-4">
              <Title incident={row.incident} />
            </td>
            <ReportedCell incident={row.incident} />
            {outcomes && (
              <>
                <td className="py-2.5 text-right">
                  <ElapsedCell
                    minutes={row.incident.responseMinutes}
                    outcome={row.incident.responseOutcome}
                    targetHours={row.incident.target?.responseHours ?? null}
                    pending={DASH}
                  />
                </td>
                {/* Always a dash here; the column is kept so the two tables read as one down the page. */}
                <td className="py-2.5 text-right text-muted-foreground">{DASH}</td>
              </>
            )}
            <HoursCell hours={row.incident.hours} className="pr-6" />
            <StateCell incident={row.incident} />
            {actions && <StateActionsCell>{actions(row)}</StateActionsCell>}
            {onOpen && <GoCell />}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/** What is done with: when it came in, when it was fixed, and whether each promise was kept. */
export const ResolvedIncidentTable = ({
  rows,
  showProduct,
  outcomes = true,
  onOpen,
  actions,
}: TableProps<IncidentView> & {
  // Whether to carry how each promise went, against its target.
  outcomes?: boolean;
}): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  return (
    <table className={tableClass}>
      <Columns
        shares={
          outcomes
            ? reportShares(showProduct, !!actions, !!onOpen)
            : [
                ['priority', 1],
                ...(showProduct ? ([['product', 1.5]] as [string, number][]) : []),
                ['what', 2.8],
                ['reported', 1.8],
                ['closed', 1.4],
                ['hours', 1.4],
                ...(actions ? ([['stateActions', 1.7]] as [string, number][]) : []),
                ...(onOpen ? ([['go', 0.4]] as [string, number][]) : []),
              ]
        }
      />
      <thead>
        <tr className={headClass}>
          <th className={cx('pb-2 pr-4 text-left font-semibold', FIRST_CELL)}>{t('incidents.priority')}</th>
          {showProduct && <th className="pb-2 pr-4 text-left font-semibold">{t('incidents.product')}</th>}
          <th className="pb-2 pr-4 text-left font-semibold">{t('incidents.issueColumn')}</th>
          <th className="pb-2 pr-6 text-right font-semibold">{t('incidents.reportedAt')}</th>
          {/* No Closed column on a report: the state column at the end already carries that timestamp. */}
          {!outcomes && <th className="pb-2 text-right font-semibold">{t('incidents.closed')}</th>}
          {outcomes && (
            <>
              <th className="pb-2 text-right font-semibold">{t('incidents.answeredIn')}</th>
              <th className="pb-2 text-right font-semibold">{t('incidents.resolvedIn')}</th>
            </>
          )}
          <th className={cx('pb-2 text-right font-semibold', outcomes && 'pr-6')}>{t('incidents.hoursReported')}</th>
          {outcomes && <th className="pb-2 text-right font-semibold">{t('status')}</th>}
          {actions && (
            <th className="pb-2 print:hidden">
              <span className="sr-only">{t('incidents.rowActions')}</span>
            </th>
          )}
          {onOpen && (
            <th className={cx('pb-2 print:hidden', LAST_CELL)}>
              <span className="sr-only">{t('incidents.showWork')}</span>
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr
            key={rowKey(row)}
            className={cx('incident-row', onOpen && 'cursor-pointer', onOpen && 'hover:bg-muted/40')}
            onClick={() => onOpen?.(row)}
          >
            <td className={cx('py-2.5 pr-4', FIRST_CELL)}>
              <PriorityBadge priority={row.incident.priority} />
            </td>
            {showProduct && <td className="truncate py-2.5 pr-4 text-muted-foreground">{row.productName}</td>}
            <td className="py-2.5 pr-4">
              <Title incident={row.incident} />
            </td>
            <ReportedCell incident={row.incident} />
            {!outcomes && (
              <td className="py-2.5 whitespace-nowrap text-right tabular-nums text-muted-foreground">
                {/* When the record was filed, not when the thing was fixed: the two are days apart. */}
                {row.incident.closedAt ? dateTime(row.incident.closedAt, language) : DASH}
              </td>
            )}
            {outcomes && (
              <>
                <td className="py-2.5 text-right">
                  <ElapsedCell
                    minutes={row.incident.responseMinutes}
                    outcome={row.incident.responseOutcome}
                    targetHours={row.incident.target?.responseHours ?? null}
                  />
                </td>
                <td className="py-2.5 text-right">
                  <ElapsedCell
                    minutes={row.incident.resolutionMinutes}
                    outcome={row.incident.resolutionOutcome}
                    targetHours={row.incident.target?.resolutionHours ?? null}
                  />
                </td>
              </>
            )}
            <HoursCell hours={row.incident.hours} className={cx(outcomes && 'pr-6')} />
            {/* The same state column the open half carries; here it is the Resolved pill. */}
            {outcomes && <StateCell incident={row.incident} at />}
            {actions && <StateActionsCell>{actions(row)}</StateActionsCell>}
            {onOpen && <GoCell />}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
